import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, IconButton, InputAdornment, Paper, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { LayerConfiguration } from '../../models/maps/layerConfiguration';
import { PagedResult } from '../../models/pagedResult';
import { LayerType } from '../../models/types/layerType';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import MaStatusSwitch from '../controls/MaStatusSwitch';
import DialogConfirm from '../controls/dialogs/DialogConfirm';

import { authService } from '../../services/auth.service';
import { planManagementService } from '../../services/plan-management.service';
import VisibilityLayersActionMenu from './VisibilityLayersActionMenu';
import { ListParams } from '../../models/listParams';
import { pageSizeStorageKey } from '../../services/utils/localStorageKeys';
import { usePersistedPaginationModel } from '../../hooks/usePersistedPageSize';


const CustomNoRowsOverlay = () => {
  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 2,
        backgroundColor: "transparent",
      }}
      elevation={0}
    >
      <Typography variant="body1">Niets gevonden</Typography>
    </Paper>
  );
};

interface VisibilityLayersProps {
  layerType: LayerType;
}

interface AssetLayerConfigurationItem {
  id: string;
  sourceOrganizationId?: string;
  sourceName?: string;
  layerDisplayName: string;
  description?: string;
  metadataUrl?: string;
  canUse: boolean;
}

interface PublicLayerConfigurationItem extends LayerConfiguration {
  mainThema?: string;
  subThema?: string;
  subtheme?: string;
}

const VisibilityLayers: React.FC<VisibilityLayersProps> = ({
  layerType,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [layerConfiguraties, setLayerConfiguraties] = useState<LayerConfiguration[]>([]);

  const [searchText, setSearchText] = useState(() =>
    layerType === LayerType.PUBLIC ? (searchParams.get('zoek') ?? '') : ''
  );
  const [totalCount, setTotalCount] = useState(0);
  const { paginationModel, setPaginationModel, handlePaginationModelChange } = usePersistedPaginationModel(pageSizeStorageKey('maps.visibility.layers'));

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [selectedLayerConfiguratie, setSelectedLayerConfiguratie] = useState<LayerConfiguration | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    snackbar: false,
    visibility: false,
    visibility_off: false,
  });

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    // setSelectedId(null);
  };

  const mapAssetLayerConfiguration = (layer: AssetLayerConfigurationItem): LayerConfiguration => ({
    id: layer.id ?? '',
    organizationId: layer.sourceOrganizationId ?? '',
    canUse: layer.canUse,
    url: '',
    sourceName: layer.sourceName ?? '',
    layerDisplayName: layer.layerDisplayName,
    description: layer.description,
    metadataUrl: layer.metadataUrl,
    type: 'geopackage',
  });

  const mapPublicLayerConfiguration = (layer: PublicLayerConfigurationItem): LayerConfiguration => ({
    ...layer,
    mainTheme: layer.mainTheme ?? layer.mainThema,
    subTheme: layer.subTheme ?? layer.subThema ?? layer.subtheme,
  });

  const fetchLayerConfiguraties = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        _offset: paginationModel.page * paginationModel.pageSize,
        _limit: paginationModel.pageSize,
        _search: searchText.trim() ? searchText.trim() : undefined,
      };

      if (layerType === LayerType.ASSET) {
        const data = await planManagementService.getLayerConfigurations(layerType, params) as PagedResult<AssetLayerConfigurationItem>;
        setLayerConfiguraties(data.items.map(mapAssetLayerConfiguration));
        setTotalCount(data.count);
      }
      else {
        const data = await planManagementService.getLayerConfigurations(layerType, params) as PagedResult<PublicLayerConfigurationItem>;
        setLayerConfiguraties(data.items.map(mapPublicLayerConfiguration));
        setTotalCount(data.count);
      }

      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchText, layerType]);

  const handleToggleVisibility = async () => {
    if (!selectedLayerConfiguratie?.id) {
      return;
    }

    try {
      setLoading(true);
      if (selectedLayerConfiguratie.canUse) {
        await planManagementService.disableLayerConfiguration(layerType, selectedLayerConfiguratie.id);
      }
      else {
        await planManagementService.enableLayerConfiguration(layerType, selectedLayerConfiguratie.id);
      }

      await fetchLayerConfiguraties();
      closeDialog(selectedLayerConfiguratie.canUse ? 'visibility_off' : 'visibility');
      setSelectedLayerConfiguratie(null);
    }
    catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Er is een fout opgetreden bij het wijzigen van de zichtbaarheid');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready) return;
    fetchLayerConfiguraties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, fetchLayerConfiguraties]);

  const handleRefresh = useCallback(async () => {
    await fetchLayerConfiguraties();
  }, [fetchLayerConfiguraties]);

  const handleRequestToggleVisibility = (
    event: React.SyntheticEvent<HTMLElement>,
    row: LayerConfiguration
  ) => {
    event.stopPropagation();

    if (!authService.isKeyUser()) {
      return;
    }

    if (!row?.id) {
      return;
    }

    setSelectedLayerConfiguratie(row);
    openDialog(row.canUse ? 'visibility_off' : 'visibility');
  };

  const columns = useMemo<GridColDef<LayerConfiguration>[]>(() => {
    const baseColumns: GridColDef<LayerConfiguration>[] = [
      {
        field: "icon",
        headerName: "Zichtbaarheid",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const isActive = params.row.canUse;
          const canToggle = authService.isKeyUser();
          const tooltipText = isActive
            ? (canToggle ? 'Klik om kaartlaag niet zichtbaar te maken' : 'Deze kaartlaag is op dit moment zichtbaar')
            : (canToggle ? 'Klik om kaartlaag zichtbaar te maken' : 'Deze kaartlaag is op dit moment niet zichtbaar');

          return (
            <div className="status-icon">
              <Tooltip title={tooltipText} placement="right" arrow>
                <span>
                  <MaStatusSwitch
                    checked={isActive}
                    offIcon="visibility_off"
                    onIcon="visibility"
                    offThumbColor="#fafafa"
                    onThumbColor="#e4f3e5"
                    offIconColor="#9e9e9e"
                    onIconColor="#2e7d32"
                    offTrackColor="#aab4be"
                    onTrackColor="#aab4be"
                    disabled={!canToggle}
                    onChange={(event) => handleRequestToggleVisibility(event, params.row)}
                    onClick={(event) => event.stopPropagation()}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        field: 'sourceName',
        headerName: 'Bron',
        flex: 2,
      },
    ];

    if (layerType === LayerType.PUBLIC) {
      baseColumns.push(
        {
          field: 'mainTheme',
          headerName: 'Hoofdthema',
          flex: 2,
        },
        {
          field: 'subTheme',
          headerName: 'Subthema',
          flex: 2,
        }
      );
    }

    baseColumns.push({
      field: 'layerDisplayName',
      headerName: 'Schermnaam',
      flex: 3,
    });

    baseColumns.push({
      field: "type",
      headerName: "Type",
      flex: 1,
      renderCell: (params: any) => {
        const value = (params.value || '').toLowerCase();
        const variantClass = `type-badge-${value}`;
        return (
          <span className={`type-badge ${variantClass}`}>
            {value}
          </span>
        );
      },
    });

    if (layerType === LayerType.PUBLIC) {
      baseColumns.push({
        field: 'organizationId',
        headerName: 'Organisatie',
        flex: 3,
      });

      // baseColumns.push({
      //   field: 'url',
      //   headerName: 'Url',
      //   flex: 5,
      // });
    }

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<LayerConfiguration>) => (
        <>
          <VisibilityLayersActionMenu params={params} layerType={layerType} onRefresh={handleRefresh} />
        </>
      ),
    });

    return baseColumns;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerType, handleRefresh]);

  useEffect(() => {
    setReady(true);
  }, []);

  const clearSearchText = async () => {
    setSearchText('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    handlePaginationModelChange({ page, pageSize });
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <div className="grid-responsive">

      {layerType === LayerType.PUBLIC && (
        <div className="search"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}>
          <TextField
            id="search"
            label="Zoeken in kaartlagen"
            variant="filled"
            placeholder="Zoeken op naam, bron of url"
            value={searchText ?? ""}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                fetchLayerConfiguraties(); // Zoek bij Enter
              }
            }}
            InputProps={{
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      clearSearchText();
                    }}
                    sx={{
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: '100%', md: '35%' },
              backgroundColor: "#fff", // witte achtergrond
              "& .MuiFilledInput-root": {
                backgroundColor: "#fff",
                borderRadius: "4px",
                "&:hover": { backgroundColor: "#fff" },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
              "& .MuiFilledInput-input": {
                paddingRight: "32px", // ruimte voor het clear-icoon
              },
            }}
          />
        </div>
      )}

        <DataGrid
          rows={layerConfiguraties}
          columns={columns}
          getRowId={(row) => row.id || ''}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onChangePagination}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            border: 0,
          }}
        />
      </div>

      <DialogConfirm
        open={open.visibility}
        title={'Kaartlaag zichtbaar maken'}
        contentText={'Weet je zeker dat je deze kaart zichtbaar wilt maken?'}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => {
          closeDialog('visibility');
          setSelectedLayerConfiguratie(null);
        }}
        onConfirm={handleToggleVisibility}
      />

      <DialogConfirm
        open={open.visibility_off}
        title={'Kaartlaag niet zichtbaar maken'}
        contentText={'Weet je zeker dat je deze kaart niet zichtbaar wilt maken?'}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => {
          closeDialog('visibility_off');
          setSelectedLayerConfiguratie(null);
        }}
        onConfirm={handleToggleVisibility}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default VisibilityLayers;
